import { createApi } from "@reduxjs/toolkit/query/react";
import { cacheDurationSeconds } from "common/constants";
import { baseQueryWithReauth } from "./utils/constants";
import {
	DashboardParams,
	EnergyResponse,
	GenericResponse,
	GHGResponse,
	PowerResponse,
	SummaryResponse,
} from "./utils/interfaces";

const getGenericURLParams = (args: DashboardParams) =>
	`userName=${args.userName}`;

const getExtendedURLParams = (args: DashboardParams) =>
	`${getGenericURLParams(args)}&startDate=${args.startDate}&endDate=${args.endDate
	}`;

const getUrlParams = (args: any) => {
	return `locationId.stationId=${args.stationId}`
}

const getTimeParams = (args: DashboardParams) => `startDate=${args.startDate}&endDate=${args.endDate}`;

const getIdTimeParams = (args: DashboardParams) => `${getUrlParams(args)}&startDate=${args.startDate}&endDate=${args.endDate}`;

export const dashboardApi = createApi({
	reducerPath: "dashboard",
	baseQuery: baseQueryWithReauth,
	keepUnusedDataFor: cacheDurationSeconds,
	tagTypes: ["SummaryStats"],
	endpoints: (builder) => ({
		getMenu: builder.query<any, DashboardParams>({
			query: (args) => {
				return ({
					url: `get-menu`,
				})
			},
			providesTags: ["SummaryStats"]
		}),
		getStation: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-stations`,
			}),
		}),
		getPowerForStation: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-power-for-station-v2?${getIdTimeParams(args)}`,
			}),
		}),
		getConnectorSummary: builder.query<GenericResponse, DashboardParams>({
			query: (args) => ({
				url: `get-connector-summary-for-all?${getGenericURLParams(args)}`,
			}),
		}),
		getSummaryStatsForStation: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-summary-stats-for-station?${getUrlParams(args)}`,
			}),
			providesTags: ["SummaryStats"]
		}),
		getConnectorSummaryForAllStationV2: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-connector-summary-for-all-v2`,
			}),
			providesTags: ["SummaryStats"]
		}),
		getConnectorSummaryForStationV2: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-connector-summary-for-station-v2?${getUrlParams(args)}`,
			}),
			providesTags: ["SummaryStats"]
		}),
		getSummaryStats: builder.query<SummaryResponse, DashboardParams>({
			query: () => ({
				url: `get-summary-stats-for-all`,
			}),
			providesTags: ["SummaryStats"]
		}),
		getEnergyDetails: builder.query<EnergyResponse, DashboardParams>({
			query: (args) => ({
				url: `get-energy-for-all?${getTimeParams(args)}`,
			}),
		}),
		getEnergyForAllV2: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-energy-for-all-v2?${getTimeParams(args)}`,
			}),
		}),
		getEnergyForStationV2: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-energy-for-station-v2?${getIdTimeParams(args)}`,
			}),
		}),
		getTransactionDetails: builder.query<GenericResponse, DashboardParams>({
			query: (args) => ({
				url: `get-transaction-for-all?${getTimeParams(args)}`,
			}),
		}),
		getTransactionForAllV2: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-transaction-for-all-v2?${getTimeParams(args)}`,
			}),
		}),
		getTransactionForStationV2: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-transaction-for-station-v2?${getIdTimeParams(args)}`,
			}),
		}),
		getUtilizationForAllV2: builder.query<GenericResponse, DashboardParams>({
			query: (args) => ({
				url: `get-utilization-for-all-v2?${getExtendedURLParams(args)}`,
			}),
		}),
		getUtilizationForStationV2: builder.query<GenericResponse, DashboardParams>({
			query: (args) => ({
				url: `get-utilization-for-station-v2?${getIdTimeParams(args)}`,
			}),
		}),
		getUtilizationDetails: builder.query<GenericResponse, DashboardParams>({
			query: (args) => ({
				url: `get-utilization-for-all?${getExtendedURLParams(args)}`,
			}),
		}),
		getRevenueDetails: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-revenue-for-all?${getTimeParams(args)}`,
			}),
		}),
		getRevenueForStation: builder.query<any, DashboardParams>({
			query: (args) => ({
				url: `get-revenue-for-station?${getIdTimeParams(args)}`,
			}),
		}),
		getGHGDetails: builder.query<GHGResponse, DashboardParams>({
			query: (args) => ({
				url: `get-ghg-reduction-for-all?${getExtendedURLParams(args)}`,
			}),
		}),
		getPowerDetails: builder.query<PowerResponse, DashboardParams>({
			query: (args) => ({
				url: `get-power-for-all?${getExtendedURLParams(args)}`,
			}),
		}),
		getActiveStats: builder.query<string[], DashboardParams>({
			query: (args) => ({
				url: `get-active-stats?${getGenericURLParams(args)}`,
			}),
		}),
		getInactiveStats: builder.query<string[], DashboardParams>({
			query: (args) => ({
				url: `get-inactive-stats?${getGenericURLParams(args)}`,
			}),
		}),
		saveStationName: builder.mutation<any, DashboardParams>({
			query: (data) => {
				const { specifyStationName, stationId } = data;
				return {
					url: `save-station-name`,
					method: "PUT",
					body: { stationName: specifyStationName, "locationId.stationId": stationId }
				};
			},
			invalidatesTags: ["SummaryStats"]
		}),
		saveStats: builder.mutation<void, DashboardParams>({
			query: (data) => {
				const { userName, activeStats, inactiveStats } = data;
				return {
					url: `save-stats?userName=${userName}`,
					method: "PUT",
					body: { activeStats: activeStats, inactiveStats: inactiveStats }
				};
			},
			invalidatesTags: ["SummaryStats"]
		})
	}),
});

export const {
	useGetMenuQuery,
	useGetStationQuery,
	useGetPowerForStationQuery,
	useGetRevenueForStationQuery,
	useGetTransactionForStationV2Query,
	useGetEnergyForStationV2Query,
	useGetTransactionForAllV2Query,
	useGetUtilizationForAllV2Query,
	useGetUtilizationForStationV2Query,
	useGetEnergyForAllV2Query,
	useGetEnergyDetailsQuery,
	useGetSummaryStatsForStationQuery,
	useGetConnectorSummaryForAllStationV2Query,
	useGetConnectorSummaryForStationV2Query,
	useGetTransactionDetailsQuery,
	useGetGHGDetailsQuery,
	useGetRevenueDetailsQuery,
	useGetUtilizationDetailsQuery,
	useGetSummaryStatsQuery,
	useGetConnectorSummaryQuery,
	useGetActiveStatsQuery,
	useGetInactiveStatsQuery,
	useSaveStationNameMutation,
	useSaveStatsMutation,
	useGetPowerDetailsQuery
} = dashboardApi;
